import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { 
  AlertTriangle, 
  HeartPulse, 
  Clock, 
  TrendingUp, 
  ArrowRight,
  Smartphone,
  Eye,
  Crown,
  RefreshCw,
  Zap,
  ThumbsUp,
  ThumbsDown,
  Building2,
  Star
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const [kpi, setKpi] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [recentIssues, setRecentIssues] = useState([]);
  const [departmentScorecard, setDepartmentScorecard] = useState([]);
  const [sentimentAudit, setSentimentAudit] = useState([]);
  const [criticalCluster, setCriticalCluster] = useState({ total_critical: 0, issues: [] });
  const [loading, setLoading] = useState(true);
  const [selectedDept, setSelectedDept] = useState(null);
  const [deptIssues, setDeptIssues] = useState([]);
  const [deptDialogOpen, setDeptDialogOpen] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [kpiRes, predRes, issuesRes, scorecardRes, auditRes, criticalRes] = await Promise.all([
        axios.get(`${API}/dashboard/kpi`),
        axios.get(`${API}/dashboard/predictions`),
        axios.get(`${API}/issues?limit=5`),
        axios.get(`${API}/dashboard/department-scorecard`),
        axios.get(`${API}/dashboard/sentiment-audit`),
        axios.get(`${API}/dashboard/critical-cluster`),
      ]);
      setKpi(kpiRes.data);
      setPredictions(predRes.data);
      setRecentIssues(issuesRes.data);
      setDepartmentScorecard(scorecardRes.data);
      setSentimentAudit(auditRes.data);
      setCriticalCluster(criticalRes.data);
    } catch (err) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleDeptClick = async (dept) => {
    setSelectedDept(dept);
    try {
      const res = await axios.get(`${API}/dashboard/department-issues/${dept}`);
      setDeptIssues(res.data);
      setDeptDialogOpen(true);
    } catch (err) {
      toast.error("Failed to load department issues");
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      S4: "bg-red-500/10 text-red-500 border-red-500/20",
      S3: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      S2: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      S1: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      P1: "bg-purple-500/10 text-purple-500 border-purple-500/20",
    };
    return colors[severity] || colors.S2;
  };

  const getStatusColor = (status) => {
    const colors = {
      OPEN: "bg-red-500/10 text-red-500 border-red-500/20",
      ACKNOWLEDGED: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      IN_PROGRESS: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      RESOLVED: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      CLOSED: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
    };
    return colors[status] || colors.OPEN;
  };

  const getDeptFullName = (code) => {
    const names = {
      ENG: "Engineering",
      HK: "Housekeeping",
      FO: "Front Office",
      FNB: "F&B",
      SEC: "Security",
      IT: "IT",
      MGT: "Management"
    };
    return names[code] || code;
  };

  const highlightText = (text) => {
    if (!text) return "";
    const defectWords = /\b(mati|rusak|bocor|kotor|bau|dingin|hambar|lama|telat|salah|macet|error|broken|dirty|slow|wrong|bad|terrible|awful|poor)\b/gi;
    const praiseWords = /\b(bagus|baik|ramah|cepat|bersih|enak|lezat|mantap|helpful|good|clean|fast|great|excellent|menikmati|amazing|wonderful|perfect|friendly)\b/gi;
    
    let result = text;
    result = result.replace(defectWords, '<span class="text-red-500 font-semibold">$1</span>');
    result = result.replace(praiseWords, '<span class="text-emerald-500 font-semibold">$1</span>');
    
    return <span dangerouslySetInnerHTML={{ __html: result }} />;
  };

  if (loading && !kpi) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-primary animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-foreground">
              Executive Cockpit
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Real-time operational intelligence
            </p>
          </div>
          <Button onClick={fetchData} variant="outline" size="sm" data-testid="refresh-btn">
            <RefreshCw size={14} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* 5.1 KPI Tiles */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="orbit-card border-l-4 border-l-red-500" data-testid="kpi-vip-risk">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                    VIP Risk Index
                  </p>
                  <p className="font-heading text-4xl font-bold text-red-500">
                    {kpi?.vip_risk_index || 0}
                  </p>
                </div>
                <div className="w-10 h-10 bg-red-500/10 rounded-sm flex items-center justify-center">
                  <Crown className="w-5 h-5 text-red-500" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Active VIP issue score</p>
            </CardContent>
          </Card>

          <Card className="orbit-card border-l-4 border-l-emerald-500" data-testid="kpi-health">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                    Health Index
                  </p>
                  <p className="font-heading text-4xl font-bold text-emerald-500">
                    {kpi?.property_health_index || 100}%
                  </p>
                </div>
                <div className="w-10 h-10 bg-emerald-500/10 rounded-sm flex items-center justify-center">
                  <HeartPulse className="w-5 h-5 text-emerald-500" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Property operational health</p>
            </CardContent>
          </Card>

          <Card className="orbit-card border-l-4 border-l-amber-500" data-testid="kpi-sla">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                    SLA Breaches
                  </p>
                  <p className="font-heading text-4xl font-bold text-amber-500">
                    {kpi?.sla_breach_count || 0}
                  </p>
                </div>
                <div className="w-10 h-10 bg-amber-500/10 rounded-sm flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-500" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Avg response: {kpi?.avg_response_time || 0} min</p>
            </CardContent>
          </Card>

          <Card className="orbit-card border-l-4 border-l-primary" data-testid="kpi-sentiment">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                    Sentiment Index
                  </p>
                  <p className="font-heading text-4xl font-bold text-primary">
                    {kpi?.sentiment_index?.toFixed(1) || "0.0"}
                  </p>
                </div>
                <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-primary" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Guest satisfaction (0-5)</p>
            </CardContent>
          </Card>
        </div>

        {/* 5.1 Critical Risk Cluster */}
        {criticalCluster.total_critical > 0 && (
          <Card className="orbit-card border-red-500/50 bg-red-500/5" data-testid="critical-cluster">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-sm uppercase tracking-wider flex items-center gap-2 text-red-500">
                <Zap className="w-4 h-4" />
                Critical Risk Cluster ({criticalCluster.total_critical})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {criticalCluster.issues.slice(0, 6).map((issue) => (
                  <Link
                    key={issue.id}
                    to={`/issues/${issue.id}`}
                    className="p-3 bg-background/50 border border-red-500/20 rounded-sm hover:border-red-500/50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-xs text-primary">{issue.issue_number}</span>
                      <Badge className={getSeverityColor(issue.final_severity)}>{issue.final_severity}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-1">
                      Room {issue.room_number} • {issue.guest_name}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      {issue.guest_type === "VIP" && <Crown size={12} className="text-purple-400" />}
                      {issue.sla_breached && <Clock size={12} className="text-amber-500" />}
                      <span className="text-xs text-red-500 font-medium">Risk: {issue.risk_score}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* 5.2 Department Scorecard */}
        <Card className="orbit-card" data-testid="department-scorecard">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-sm uppercase tracking-wider flex items-center gap-2">
              <Building2 className="w-4 h-4 text-primary" />
              Department Scorecard
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {departmentScorecard.map((dept) => (
                <button
                  key={dept.department}
                  onClick={() => handleDeptClick(dept.department)}
                  className="p-4 bg-surface-highlight/50 border border-border rounded-sm hover:border-primary/50 transition-all text-left group"
                  data-testid={`dept-${dept.department}`}
                >
                  <p className="font-heading text-lg font-bold text-foreground group-hover:text-primary transition-colors">
                    {dept.department}
                  </p>
                  <p className="text-[10px] text-muted-foreground mb-3">{getDeptFullName(dept.department)}</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <ThumbsUp size={10} className="text-emerald-500" />
                        <span className="text-xs text-emerald-500">{dept.praise_points}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <ThumbsDown size={10} className="text-red-500" />
                        <span className="text-xs text-red-500">{dept.defect_points}</span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-surface rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400"
                        style={{ width: `${dept.score}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-center text-muted-foreground">{dept.score}% positive</p>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* 5.3 Sentiment Audit (TrustYou Style) */}
        <Card className="orbit-card" data-testid="sentiment-audit">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-sm uppercase tracking-wider flex items-center gap-2">
              <Star className="w-4 h-4 text-primary" />
              Sentiment Audit
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left text-xs uppercase tracking-wider text-muted-foreground py-3 px-2">Category</th>
                    <th className="text-center text-xs uppercase tracking-wider text-muted-foreground py-3 px-2">Score</th>
                    <th className="text-center text-xs uppercase tracking-wider text-muted-foreground py-3 px-2">Mentions</th>
                    <th className="text-left text-xs uppercase tracking-wider text-muted-foreground py-3 px-2 w-1/3">Distribution</th>
                  </tr>
                </thead>
                <tbody>
                  {sentimentAudit.map((item) => (
                    <tr key={item.category} className="border-b border-border/50 hover:bg-surface-highlight/30">
                      <td className="py-3 px-2">
                        <span className="text-sm font-medium">{item.category}</span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className={`font-heading text-xl font-bold ${
                          item.avg_score >= 4 ? 'text-emerald-500' :
                          item.avg_score >= 3 ? 'text-primary' :
                          item.avg_score >= 2 ? 'text-amber-500' : 'text-red-500'
                        }`}>
                          {item.avg_score}
                        </span>
                        <span className="text-xs text-muted-foreground">/5</span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className="text-sm text-muted-foreground">{item.total_mentions}</span>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-4 bg-surface rounded-full overflow-hidden flex">
                            <div 
                              className="h-full bg-emerald-500"
                              style={{ width: `${item.positive_pct}%` }}
                              title={`Positive: ${item.positive_pct}%`}
                            />
                            <div 
                              className="h-full bg-zinc-600"
                              style={{ width: `${100 - item.positive_pct - item.negative_pct}%` }}
                              title="Neutral"
                            />
                            <div 
                              className="h-full bg-red-500"
                              style={{ width: `${item.negative_pct}%` }}
                              title={`Negative: ${item.negative_pct}%`}
                            />
                          </div>
                          <div className="flex gap-2 text-[10px] min-w-[80px]">
                            <span className="text-emerald-500">{item.positive}</span>
                            <span className="text-zinc-500">{item.neutral}</span>
                            <span className="text-red-500">{item.negative}</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 5.5 Predictive Alerts (GM Exclusive) */}
          <Card className="orbit-card lg:col-span-1" data-testid="predictive-alerts">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-sm uppercase tracking-wider flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Predictive Alerts
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {predictions.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No predictions yet
                </p>
              ) : (
                predictions.slice(0, 4).map((pred) => (
                  <div
                    key={pred.id}
                    className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-sm"
                  >
                    <p className="text-xs text-amber-500 font-medium">{pred.message}</p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-[10px] text-muted-foreground">
                        Frequency: {pred.frequency}x
                      </p>
                      <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 text-[10px]">
                        {Math.round(pred.confidence * 100)}% confidence
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* 5.4 Command Queue */}
          <Card className="orbit-card lg:col-span-2" data-testid="command-queue">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-sm uppercase tracking-wider">
                Command Queue
              </CardTitle>
              <Link to="/issues">
                <Button variant="ghost" size="sm" className="text-primary">
                  View All <ArrowRight size={14} className="ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {recentIssues.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No issues found</p>
                ) : (
                  recentIssues.map((issue) => (
                    <Link
                      key={issue.id}
                      to={`/issues/${issue.id}`}
                      className="block p-3 bg-surface-highlight/50 border border-border rounded-sm hover:border-primary/50 transition-colors"
                      data-testid={`issue-${issue.issue_number}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-xs text-primary">{issue.issue_number}</span>
                          <Badge className={getSeverityColor(issue.final_severity)}>{issue.final_severity}</Badge>
                          {issue.guest_type === "VIP" && (
                            <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20">
                              <Crown size={10} className="mr-1" /> VIP
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {issue.notified_at && (
                            <Smartphone size={14} className="text-blue-400" title="Notification sent" />
                          )}
                          {issue.first_response_at && (
                            <Eye size={14} className="text-emerald-400" title="Acknowledged" />
                          )}
                          <Badge className={getStatusColor(issue.status)}>{issue.status}</Badge>
                        </div>
                      </div>
                      <p className="text-sm mt-2 text-muted-foreground line-clamp-1">
                        Room {issue.room_number} • {issue.guest_name}
                      </p>
                    </Link>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Department Drill-Down Dialog */}
        <Dialog open={deptDialogOpen} onOpenChange={setDeptDialogOpen}>
          <DialogContent className="bg-card border-border max-w-3xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase tracking-wider flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                {getDeptFullName(selectedDept)} Issues
              </DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-3 pr-4">
                {deptIssues.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No issues found</p>
                ) : (
                  deptIssues.map((issue) => (
                    <Link
                      key={issue.id}
                      to={`/issues/${issue.id}`}
                      onClick={() => setDeptDialogOpen(false)}
                      className="block p-4 bg-surface-highlight/50 border border-border rounded-sm hover:border-primary/50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-primary">{issue.issue_number}</span>
                          <Badge className={getSeverityColor(issue.final_severity)}>{issue.final_severity}</Badge>
                          <Badge className={getStatusColor(issue.status)}>{issue.status}</Badge>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(issue.system_logged_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm mb-1">Room {issue.room_number} • {issue.guest_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {highlightText(issue.description)}
                      </p>
                    </Link>
                  ))
                )}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Dashboard;
